/**
 * Stock-level list widgets (#1805 slice A1).
 *
 * The four projections `stockLevelResource.tsx` used to declare inline as
 * `fields[].render`. See `features/stocktake/widgets.tsx` for why each one
 * carries `csvText: stringifyRaw(value)` and why the markup is copied verbatim
 * rather than rewritten.
 *
 * None of these reads the record: every one is a pure function of the cell
 * value, and `stockOnHand` is a pure constant.
 */
import { Badge, stringifyRaw } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    stockIngredientName: { types: 'text'; modes: 'list' };
    stockUnit: { types: 'text'; modes: 'list' };
    stockCategory: { types: 'text'; modes: 'list' };
    stockOnHand: { types: 'text'; modes: 'list' };
  }
}

// The on-hand column is a permanent em-dash (no batch endpoint yet, #935/#936).
const ON_HAND_PLACEHOLDER = '—';
// Empty-optional-field marker (category fallback). Kept separate from the
// on-hand marker so #935/#936 replacing the latter never changes the former.
const EMPTY_VALUE = '—';

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

function StockIngredientName({ value }: WidgetProps) {
  return <span className="font-medium">{String(value ?? '')}</span>;
}

function StockUnit({ value }: WidgetProps) {
  return <Badge variant="outline">{String(value ?? '')}</Badge>;
}

function StockCategory({ value }: WidgetProps) {
  return <span className="text-muted-foreground">{value ? String(value) : EMPTY_VALUE}</span>;
}

/** Display-only degraded column: no accessor value, a fixed em-dash. */
function StockOnHand() {
  return <span className="tabular-nums text-muted-foreground">{ON_HAND_PLACEHOLDER}</span>;
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerInventoryWidgets(): void {
  registerWidget('stockIngredientName', {
    display: StockIngredientName,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('stockUnit', {
    display: StockUnit,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('stockCategory', {
    display: StockCategory,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('stockOnHand', {
    display: StockOnHand,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
}
